import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Lock, AlertTriangle } from 'lucide-react';

const AgeGate = ({ onVerify }) => {
  const [isVisible, setIsVisible] = useState(true);
  
  useEffect(() => {
    const verified = localStorage.getItem('age_verified');
    if (verified === 'true') {
      setIsVisible(false);
      onVerify && onVerify();
    }
  }, [onVerify]);

  const handleVerify = () => {
    localStorage.setItem('age_verified', 'true');
    setIsVisible(false);
    onVerify && onVerify();
  };

  const handleDeny = () => {
    window.location.href = 'https://google.com';
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 z-[9999] flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="age-gate-modal"
      >
        {/* Dark backdrop */}
        <div className="absolute inset-0 bg-black/95 backdrop-blur-xl" />
        
        {/* Modal */}
        <motion.div 
          className="relative z-10 max-w-md w-full mx-4 p-8 rounded-2xl bg-gradient-to-b from-zinc-900 to-black border border-zinc-800"
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: 'spring', damping: 25 }}
        >
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-600/30 to-fuchsia-600/30 flex items-center justify-center border border-violet-500/30">
              <Shield className="w-10 h-10 text-violet-400" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-center text-white mb-2">
            Age Verification Required
          </h2>
          
          {/* Subtitle */}
          <p className="text-zinc-400 text-center mb-6">
            This website contains adult content exclusively for individuals 18 years of age or older.
          </p>

          {/* Warning */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-6">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-200/80">
              By entering, you confirm that you are at least 18 years old and agree to our Terms of Service and Privacy Policy.
            </p>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleVerify}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold hover:from-violet-500 hover:to-fuchsia-500 transition-all duration-300 flex items-center justify-center gap-2"
              data-testid="age-verify-btn"
            >
              <Lock className="w-5 h-5" />
              I am 18+ — Enter
            </button>
            
            <button
              onClick={handleDeny}
              className="w-full py-3 rounded-xl border border-zinc-700 text-zinc-400 font-medium hover:border-zinc-600 hover:text-zinc-300 transition-all duration-300"
              data-testid="age-deny-btn"
            >
              I am under 18 — Exit
            </button>
          </div>

          {/* Legal links */}
          <div className="flex justify-center gap-4 mt-6">
            <a 
              href="/terms" 
              className="text-xs text-zinc-500 hover:text-violet-400 transition-colors"
              data-testid="terms-link"
            >
              Terms of Service
            </a>
            <span className="text-zinc-700">|</span>
            <a 
              href="/privacy" 
              className="text-xs text-zinc-500 hover:text-violet-400 transition-colors"
              data-testid="privacy-link"
            >
              Privacy Policy
            </a>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AgeGate;
